import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, GripVertical, Save, Printer, X, ArrowLeft, Download, ListPlus, Search } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import InvoiceDocument from "@/components/InvoiceDocument";
import { INVOICE_THEMES } from "@/lib/invoiceThemes";
import html2pdf from "html2pdf.js";

const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600 h-auto";
const labelCls = "font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500";

const newItem = () => ({ id: `li_${Math.random().toString(36).slice(2, 10)}`, description: "", quantity: 1, rate: 0, amount: 0 });
const newPayment = () => ({ id: `pay_${Math.random().toString(36).slice(2, 10)}`, amount: 0, method: "UPI", date: new Date().toISOString().slice(0, 10) });

export default function InvoiceEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const { activeProfileId } = useProfiles();

  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [newCatPrice, setNewCatPrice] = useState("");

  const loadCatalog = async (profileId) => {
    if (!profileId) { setCatalogItems([]); return; }
    const { data } = await api.get(`/items?profile_id=${profileId}`);
    setCatalogItems(data);
  };

  const [inv, setInv] = useState({
    invoice_number: "",
    profile_id: "",
    client_id: "",
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    items: [newItem()],
    notes: "",
    gst_rate: 18,
    gst_type: "CGST_SGST",
    advance_paid: 0,
    discount: 0,
    discount_type: "flat",
    terms: "",
    payments: [],
    status: "draft",
    theme_id: "classic-gold",
  });

  useEffect(() => {
    (async () => {
      const pr = await api.get("/profiles");
      setProfiles(pr.data);
      if (isNew) {
        const defaultProfile = pr.data.find((p) => p.id === activeProfileId)?.id || pr.data[0]?.id || "";
        const [cl, num] = await Promise.all([
          defaultProfile ? api.get(`/clients?profile_id=${defaultProfile}`) : Promise.resolve({ data: [] }),
          defaultProfile ? api.get(`/invoices/next-number?profile_id=${defaultProfile}`) : Promise.resolve({ data: { invoice_number: "" } }),
        ]);
        setClients(cl.data);
        loadCatalog(defaultProfile);
        setInv((v) => ({
          ...v,
          invoice_number: num.data.invoice_number,
          profile_id: defaultProfile,
          client_id: cl.data[0]?.id || "",
        }));
      } else {
        const { data } = await api.get(`/invoices/${id}`);
        const cl = await api.get(`/clients?profile_id=${data.profile_id}`);
        setClients(cl.data);
        loadCatalog(data.profile_id);
        setInv({
          invoice_number: data.invoice_number,
          profile_id: data.profile_id,
          client_id: data.client_id,
          issue_date: data.issue_date,
          due_date: data.due_date || "",
          items: data.items?.length ? data.items : [newItem()],
          notes: data.notes || "",
          gst_rate: data.gst_rate,
          gst_type: data.gst_type,
          advance_paid: data.advance_paid || 0,
          discount: data.discount || 0,
          discount_type: data.discount_type || "flat",
          terms: data.terms || "",
          payments: data.payments || [],
          status: data.status || "draft",
          theme_id: data.theme_id || "classic-gold",
        });
      }
    })();
  }, [id, isNew, activeProfileId]);

  // Refetch clients & catalog whenever the selected business changes (e.g. user switches "From Business")
  const initialLoadDone = React.useRef(false);
  useEffect(() => {
    if (!initialLoadDone.current) { initialLoadDone.current = true; return; }
    if (!inv.profile_id) return;
    (async () => {
      const cl = await api.get(`/clients?profile_id=${inv.profile_id}`);
      setClients(cl.data);
      loadCatalog(inv.profile_id);
      if (!cl.data.find((c) => c.id === inv.client_id)) {
        setInv((v) => ({ ...v, client_id: cl.data[0]?.id || "" }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inv.profile_id]);

  const totals = useMemo(() => {
    const subtotal = inv.items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.rate || 0), 0);
    const dval = Number(inv.discount || 0);
    const discount_amount = inv.discount_type === "percent" ? subtotal * (dval / 100) : dval;
    const taxable = Math.max(0, subtotal - discount_amount);
    const gst_amount = taxable * (Number(inv.gst_rate || 0) / 100);
    const grand_total = taxable + gst_amount;
    const paymentsSum = inv.payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const paid = paymentsSum + Number(inv.advance_paid || 0);
    const balance_due = grand_total - paid;
    return {
      subtotal: round(subtotal), discount_amount: round(discount_amount), taxable: round(taxable),
      gst_amount: round(gst_amount), grand_total: round(grand_total),
      paid: round(paid), balance_due: round(balance_due),
    };
  }, [inv]);

  const setItem = (idx, key, val) => {
    setInv((v) => {
      const items = [...v.items];
      items[idx] = { ...items[idx], [key]: val };
      items[idx].amount = Number(items[idx].quantity || 0) * Number(items[idx].rate || 0);
      return { ...v, items };
    });
  };

  const addItem = () => setInv((v) => ({ ...v, items: [...v.items, newItem()] }));
  const removeItem = (idx) => setInv((v) => ({ ...v, items: v.items.filter((_, i) => i !== idx) }));

  const addFromCatalog = (catItem) => {
    setInv((v) => ({
      ...v,
      items: [...v.items, { id: `li_${Math.random().toString(36).slice(2, 10)}`, description: catItem.name, quantity: 1, rate: catItem.sales_price, amount: catItem.sales_price }],
    }));
    toast.success(`Added "${catItem.name}"`);
  };

  const [savingCatalogItem, setSavingCatalogItem] = useState(false);
  const quickCreateCatalogItem = async () => {
    if (savingCatalogItem) return;
    if (!newCatName.trim()) { toast.error("Enter an item name"); return; }
    if (!inv.profile_id) { toast.error("Pick a business first"); return; }
    setSavingCatalogItem(true);
    try {
      const { data } = await api.post(`/items?profile_id=${inv.profile_id}`, { name: newCatName, sales_price: Number(newCatPrice || 0) });
      setCatalogItems((v) => [...v, data].sort((a, b) => a.name.localeCompare(b.name)));
      addFromCatalog(data);
      setNewCatName(""); setNewCatPrice("");
    } catch {
      toast.error("Failed to save catalog item");
    } finally {
      setSavingCatalogItem(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(inv.items);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setInv((v) => ({ ...v, items }));
  };

  const addPayment = () => setInv((v) => ({ ...v, payments: [...v.payments, newPayment()] }));
  const setPayment = (idx, key, val) => {
    setInv((v) => {
      const payments = [...v.payments];
      payments[idx] = { ...payments[idx], [key]: val };
      return { ...v, payments };
    });
  };
  const removePayment = (idx) => setInv((v) => ({ ...v, payments: v.payments.filter((_, i) => i !== idx) }));

  const [saving, setSaving] = useState(false);
  const save = async (andPreview = false) => {
    if (saving) return;
    if (!inv.profile_id || !inv.client_id) { toast.error("Pick a business and a client"); return; }
    setSaving(true);
    try {
      const payload = {
        ...inv,
        advance_paid: Number(inv.advance_paid || 0),
        discount: Number(inv.discount || 0),
        gst_rate: Number(inv.gst_rate || 0),
        items: inv.items.map((i) => ({ ...i, quantity: Number(i.quantity || 0), rate: Number(i.rate || 0), amount: Number(i.quantity || 0) * Number(i.rate || 0) })),
        payments: inv.payments.map((p) => ({ ...p, amount: Number(p.amount || 0) })),
      };
      let saved;
      if (isNew) saved = await api.post("/invoices", payload);
      else saved = await api.put(`/invoices/${id}`, payload);
      toast.success(isNew ? "Invoice created" : "Invoice updated");
      if (andPreview) setShowPreview(true);
      else if (isNew) navigate(`/invoices/${saved.data.id}`, { replace: true });
    } catch {
      toast.error("Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const profile = profiles.find((p) => p.id === inv.profile_id);
  const client = clients.find((c) => c.id === inv.client_id);

  if (showPreview) {
    const downloadPdf = () => {
      const el = document.getElementById("invoice-document-root");
      if (!el) return;
      const filename = `${inv.invoice_number || "invoice"}.pdf`;
      html2pdf().set({
        margin: 0,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      }).from(el).save();
    };
    return (
      <div className="min-h-screen bg-neutral-900 print:bg-white">
        <div className="no-print sticky top-0 z-20 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <button data-testid="close-preview-btn" onClick={() => setShowPreview(false)} className="flex items-center gap-2 text-neutral-300 hover:text-white">
            <X className="w-4 h-4" strokeWidth={1.5} /> <span className="font-mont text-xs uppercase tracking-[0.2em]">Close Preview</span>
          </button>
          <div className="flex gap-3">
            <Button data-testid="download-pdf-btn" onClick={downloadPdf} className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white/5">
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Download PDF
            </Button>
            <Button data-testid="print-pdf-btn" onClick={() => window.print()} className="rounded-none bg-white text-black hover:bg-neutral-200">
              <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print
            </Button>
          </div>
        </div>
        <div className="py-8 print:py-0">
          <InvoiceDocument invoice={inv} profile={profile} client={client} totals={totals} />
        </div>
      </div>
    );
  }

  return (
    <div className="px-10 py-10">
      <button onClick={() => navigate("/invoices")} className="flex items-center gap-2 text-neutral-500 hover:text-white mb-6 font-mont text-xs uppercase tracking-[0.25em]">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
      </button>

      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— {isNew ? "Compose" : "Revise"}</p>
          <h1 className="font-serif text-5xl tracking-tight text-white">{isNew ? "New Invoice" : inv.invoice_number}</h1>
        </div>
        <div className="flex gap-3">
          <Button data-testid="save-invoice-btn" disabled={saving} onClick={() => save(false)} className="rounded-none bg-transparent border border-white/20 text-white hover:bg-white/5 px-6 py-5 disabled:opacity-50">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button data-testid="save-preview-btn" disabled={saving} onClick={() => save(true)} className="rounded-none bg-white text-black hover:bg-neutral-200 px-6 py-5 disabled:opacity-50">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : "Save & Preview"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* LEFT: Form */}
        <div className="lg:col-span-8 space-y-10">
          {/* Profile & Client */}
          <section className="bg-[#121212] border border-white/10 p-8">
            <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6">— Identity</div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className={labelCls}>From (Business)</Label>
                <Select value={inv.profile_id} onValueChange={(v) => setInv((s) => ({ ...s, profile_id: v }))}>
                  <SelectTrigger data-testid="select-profile" className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                    <SelectValue placeholder="Select business" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                    {profiles.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Bill To (Client)</Label>
                <Select value={inv.client_id} onValueChange={(v) => setInv((s) => ({ ...s, client_id: v }))}>
                  <SelectTrigger data-testid="select-client" className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="font-mont text-sm">{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Invoice No.</Label>
                <Input data-testid="input-invoice-number" className={fieldCls} value={inv.invoice_number} onChange={(e) => setInv((v) => ({ ...v, invoice_number: e.target.value }))} />
              </div>
              <div>
                <Label className={labelCls}>Issue Date</Label>
                <Input data-testid="input-issue-date" type="date" className={fieldCls + " [color-scheme:dark]"} value={inv.issue_date} onChange={(e) => setInv((v) => ({ ...v, issue_date: e.target.value }))} />
              </div>
              <div>
                <Label className={labelCls}>Due Date</Label>
                <Input type="date" className={fieldCls + " [color-scheme:dark]"} value={inv.due_date} onChange={(e) => setInv((v) => ({ ...v, due_date: e.target.value }))} />
              </div>
            </div>
          </section>

          {/* Items */}
          <section className="bg-[#121212] border border-white/10 p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Line Items (drag to reorder)</div>
              <div className="flex gap-2">
                <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="browse-catalog-btn" variant="ghost" className="text-neutral-300 hover:text-white hover:bg-white/5 rounded-none">
                      <ListPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Browse Catalog
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#121212] border-white/10 rounded-none text-white max-w-lg max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Item Catalog</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-2">
                      <div className="relative">
                        <Search className="w-4 h-4 text-neutral-500 absolute left-0 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                        <Input
                          data-testid="catalog-search-input"
                          value={catalogSearch}
                          onChange={(e) => setCatalogSearch(e.target.value)}
                          placeholder="Search saved items…"
                          className={fieldCls + " pl-6"}
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto divide-y divide-white/10 border border-white/10">
                        {catalogItems.filter((c) => c.name.toLowerCase().includes(catalogSearch.toLowerCase())).length === 0 ? (
                          <div className="py-8 text-center font-mont text-xs uppercase tracking-[0.25em] text-neutral-600">
                            {catalogItems.length === 0 ? "No saved items yet — add one below" : "No matches"}
                          </div>
                        ) : (
                          catalogItems
                            .filter((c) => c.name.toLowerCase().includes(catalogSearch.toLowerCase()))
                            .map((c) => (
                              <button
                                key={c.id}
                                data-testid={`catalog-item-${c.name}`}
                                onClick={() => { addFromCatalog(c); setCatalogOpen(false); }}
                                className="w-full flex items-center justify-between px-3 py-3 hover:bg-white/5 text-left"
                              >
                                <span className="text-sm text-white truncate">{c.name}</span>
                                <span className="font-mont text-sm text-[#D4AF37] flex-shrink-0 ml-3">{fmtINR(c.sales_price)}</span>
                              </button>
                            ))
                        )}
                      </div>
                      <div className="pt-3 border-t border-white/10">
                        <div className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">— New catalog item</div>
                        <div className="flex gap-2">
                          <Input data-testid="new-catalog-name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Item name" className={fieldCls + " flex-1"} />
                          <Input data-testid="new-catalog-price" type="number" step="any" value={newCatPrice} onChange={(e) => setNewCatPrice(e.target.value)} placeholder="Price" className={fieldCls + " w-28"} />
                          <Button data-testid="save-catalog-item-btn" disabled={savingCatalogItem} onClick={quickCreateCatalogItem} className="rounded-none bg-white text-black hover:bg-neutral-200 flex-shrink-0 disabled:opacity-50">{savingCatalogItem ? "Saving…" : "Save & Add"}</Button>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button data-testid="add-item-btn" onClick={addItem} variant="ghost" className="text-neutral-300 hover:text-white hover:bg-white/5 rounded-none">
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add Item
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 pb-3 border-b border-white/10 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              <div className="col-span-1"></div>
              <div className="col-span-5">Description</div>
              <div className="col-span-2 text-right">Qty</div>
              <div className="col-span-2 text-right">Rate</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="items">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {inv.items.map((it, idx) => (
                      <Draggable key={it.id} draggableId={it.id} index={idx}>
                        {(p) => (
                          <div
                            ref={p.innerRef}
                            {...p.draggableProps}
                            data-testid={`item-row-${idx}`}
                            className="grid grid-cols-12 gap-3 py-4 border-b border-white/10 items-center"
                          >
                            <div className="col-span-1 flex items-center gap-2">
                              <span {...p.dragHandleProps} data-testid={`drag-handle-${idx}`} className="cursor-grab text-neutral-500 hover:text-white">
                                <GripVertical className="w-4 h-4" strokeWidth={1.5} />
                              </span>
                              <button onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="text-neutral-500 hover:text-red-400">
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </button>
                            </div>
                            <Input data-testid={`item-desc-${idx}`} value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} placeholder="Item description" className={fieldCls + " col-span-5"} />
                            <Input data-testid={`item-qty-${idx}`} type="number" step="any" value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} className={fieldCls + " col-span-2 text-right"} />
                            <Input data-testid={`item-rate-${idx}`} type="number" step="any" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} className={fieldCls + " col-span-2 text-right"} />
                            <div className="col-span-2 text-right font-mont text-sm text-white">
                              {fmtINR(Number(it.quantity || 0) * Number(it.rate || 0))}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </section>

          {/* Payments */}
          <section className="bg-[#121212] border border-white/10 p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-neutral-500">— Payments Received</div>
              <Button data-testid="add-payment-btn" onClick={addPayment} variant="ghost" className="text-neutral-300 hover:text-white hover:bg-white/5 rounded-none">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Record Payment
              </Button>
            </div>
            {inv.payments.length === 0 ? (
              <div className="font-mont text-xs text-neutral-600 uppercase tracking-[0.25em] py-4">No payments yet</div>
            ) : (
              inv.payments.map((p, idx) => (
                <div key={p.id} data-testid={`payment-row-${idx}`} className="grid grid-cols-12 gap-3 py-3 border-b border-white/10 items-center">
                  <Input type="date" value={p.date} onChange={(e) => setPayment(idx, "date", e.target.value)} className={fieldCls + " col-span-3 [color-scheme:dark]"} />
                  <Select value={p.method} onValueChange={(v) => setPayment(idx, "method", v)}>
                    <SelectTrigger className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont col-span-3 px-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                      {["UPI", "Bank Transfer", "Cash", "Cheque", "Card"].map((m) => (
                        <SelectItem key={m} value={m} className="font-mont">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" step="any" value={p.amount} onChange={(e) => setPayment(idx, "amount", e.target.value)} placeholder="Amount" className={fieldCls + " col-span-5 text-right"} />
                  <button onClick={() => removePayment(idx)} className="col-span-1 text-neutral-500 hover:text-red-400 justify-self-end">
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              ))
            )}
          </section>

          {/* Terms */}
          <section className="bg-[#121212] border border-white/10 p-8">
            <Label className={labelCls}>Terms & Conditions</Label>
            <Textarea data-testid="input-terms" value={inv.terms} onChange={(e) => setInv((v) => ({ ...v, terms: e.target.value }))}
              placeholder="e.g. Payment due within 15 days. Late payment attracts 2% interest per month."
              className="bg-transparent border border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont mt-2 min-h-[100px]" />
          </section>
        </div>

        {/* RIGHT: Summary */}
        <aside className="lg:col-span-4 space-y-6">
          <div className="bg-[#121212] border border-white/10 p-8 sticky top-6">
            <div className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">— Summary</div>

            <div className="space-y-4 mb-6">
              <div>
                <Label className={labelCls}>GST Rate (%)</Label>
                <Input data-testid="input-gst-rate" type="number" step="any" value={inv.gst_rate}
                  onChange={(e) => setInv((v) => ({ ...v, gst_rate: e.target.value }))} className={fieldCls} />
              </div>
              <div>
                <Label className={labelCls}>GST Type</Label>
                <Select value={inv.gst_type} onValueChange={(v) => setInv((s) => ({ ...s, gst_type: v }))}>
                  <SelectTrigger data-testid="select-gst-type" className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                    <SelectItem value="CGST_SGST" className="font-mont">CGST + SGST (Intra-state)</SelectItem>
                    <SelectItem value="IGST" className="font-mont">IGST (Inter-state)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Advance Paid (₹)</Label>
                <Input data-testid="input-advance-paid" type="number" step="any" value={inv.advance_paid}
                  onChange={(e) => setInv((v) => ({ ...v, advance_paid: e.target.value }))} className={fieldCls} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className={labelCls}>Discount</Label>
                  <Input data-testid="input-discount" type="number" step="any" value={inv.discount}
                    onChange={(e) => setInv((v) => ({ ...v, discount: e.target.value }))} className={fieldCls} />
                </div>
                <div>
                  <Label className={labelCls}>Type</Label>
                  <Select value={inv.discount_type} onValueChange={(v) => setInv((s) => ({ ...s, discount_type: v }))}>
                    <SelectTrigger data-testid="select-discount-type" className="bg-transparent border-0 border-b border-white/20 rounded-none focus:ring-0 text-white font-mont mt-1 px-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                      <SelectItem value="flat" className="font-mont">₹ Flat</SelectItem>
                      <SelectItem value="percent" className="font-mont">% Percent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-6 border-t border-white/10">
              <SumRow label="Subtotal" value={fmtINR(totals.subtotal)} />
              <SumRow label={`GST (${inv.gst_rate || 0}%)`} value={fmtINR(totals.gst_amount)} testId="row-gst" />
              <div className="border-t border-white/10 pt-3">
                <SumRow label="Grand Total" value={fmtINR(totals.grand_total)} accent testId="row-grand-total" />
              </div>
              {totals.paid > 0 && <SumRow label="Paid" value={"− " + fmtINR(totals.paid)} muted />}
              <div className="border-t border-[#D4AF37]/30 pt-3 mt-3">
                <SumRow label="Balance Due" value={fmtINR(totals.balance_due)} accent large testId="row-balance-due" />
              </div>
            </div>

            {/* THEME PICKER */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">— Invoice Theme</div>
              <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1">
                {INVOICE_THEMES.map((t) => {
                  const selected = inv.theme_id === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`theme-${t.id}`}
                      onClick={() => setInv((v) => ({ ...v, theme_id: t.id }))}
                      className={`relative p-1 border transition-all ${selected ? "border-[#D4AF37]" : "border-white/10 hover:border-white/30"}`}
                      title={t.name}
                    >
                      <div className="h-12 flex flex-col">
                        <div className="h-3" style={{ background: t.header || t.band }} />
                        <div className="flex-1 flex">
                          <div className="flex-1" style={{ background: t.band }} />
                          <div className="w-2" style={{ background: t.accent }} />
                        </div>
                        <div className="h-1" style={{ background: t.border }} />
                      </div>
                      <div className="mt-1 text-[8px] font-mont tracking-wide text-neutral-400 truncate">{t.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const SumRow = ({ label, value, accent, muted, large, testId }) => (
  <div data-testid={testId} className="flex items-baseline justify-between">
    <span className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</span>
    <span className={`font-mont ${large ? "text-2xl" : "text-sm"} ${accent ? "text-[#D4AF37]" : muted ? "text-neutral-500" : "text-white"}`}>{value}</span>
  </div>
);
